<template>
  <AIVDialog
    v-model="isVisible"
    :title="`Approved Shifts for Authorization ${authorizationData?.auth_number || ''}`"
    width="90%"
    height="85vh"
    class="approved-shifts-modal"
  >
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">Loading approved shifts...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else class="modal-content-wrapper">
      <!-- Summary Information -->
      <div class="mb-4 p-3 summary-info rounded">
        <div class="row">
          <div class="col-md-3">
            <strong>Total Approved Shifts:</strong> {{ shiftsData?.count || 0 }}
          </div>
          <div class="col-md-3">
            <template v-if="isDSPDGroupAuth">
              <strong>Total Applied Units:</strong> {{ shiftsData?.totalUnits || '0' }}
            </template>
            <template v-else>
              <strong>Total Applied Hours:</strong> {{ shiftsData?.totalBillingHours || '0.00' }}
            </template>
          </div>
          <div class="col-md-3">
            <strong>Authorization Number:</strong> {{ authorizationData?.auth_number || 'N/A' }}
          </div>
          <div class="col-md-3">
            <strong>Client:</strong> {{ authorizationData?.client_display_name || 'N/A' }}
          </div>
        </div>
      </div>

      <!-- Shifts Table -->
      <div v-if="shiftsData?.count > 0" class="table-wrapper">
        <AIVTable
          :columns="effectiveColumns"
          :data="shiftsData.shifts"
          :pagination="paginationObject"
          :item-name="'shifts'"
        >
          <template #cell-date="{ row }">
            {{ formatDate(row.scheduled_start) }}
          </template>

          <template #cell-time_range="{ row }">
            {{ formatTimeRange(row.scheduled_start, row.scheduled_end) }}
          </template>

          <template #cell-caregivers="{ row }">
            <div v-if="row.caregivers && row.caregivers.length > 0">
              <span
                v-for="(caregiver, index) in row.caregivers"
                :key="caregiver.id"
              >
                {{ caregiver.name }}<span v-if="index < row.caregivers.length - 1">, </span>
              </span>
            </div>
            <span v-else class="text-muted-custom">No caregiver assigned</span>
          </template>

          <template #cell-clients="{ row }">
            <div v-if="row.clients && row.clients.length > 0">
              <span
                v-for="(client, index) in row.clients"
                :key="client.id"
              >
                {{ client.name }}<span v-if="index < row.clients.length - 1">, </span>
              </span>
            </div>
            <span v-else class="text-muted-custom">No client assigned</span>
          </template>

          <template #cell-service_codes="{ row }">
            <div v-if="row.service_codes && row.service_codes.length > 0">
              <span
                v-for="(service, index) in row.service_codes"
                :key="service.id"
                :title="service.name"
              >
                {{ service.code }}<span v-if="index < row.service_codes.length - 1">, </span>
              </span>
            </div>
            <span v-else class="text-muted-custom">No service code</span>
          </template>

          <template #cell-clock_times="{ row }">
            <template v-if="isDSPDGroupAuth">
              <div v-if="row.client_pickup_time && row.client_dropoff_time">
                <small><strong>Pick Up:</strong> {{ cleanTimeString(row.client_pickup_time) }}</small><br>
                <small><strong>Drop Off:</strong> {{ cleanTimeString(row.client_dropoff_time) }}</small>
              </div>
              <span v-else class="text-muted-custom">No transport times</span>
            </template>
            <template v-else>
              <div v-if="row.clock_in && row.clock_out">
                <small><strong>In:</strong> {{ cleanTimeString(row.clock_in) }}</small><br>
                <div v-if="row.breaks && row.breaks.length > 0">
                  <span v-for="(breakItem, index) in row.breaks" :key="index">
                    <small><strong>Break:</strong> {{ formatBreakTime(breakItem.break_start, breakItem.break_end) }}</small><br>
                  </span>
                </div>
                <small><strong>Out:</strong> {{ cleanTimeString(row.clock_out) }}</small>
              </div>
              <span v-else class="text-muted-custom">No clock times</span>
            </template>
          </template>

          <template #cell-duration="{ row }">
            <template v-if="isDSPDGroupAuth">
              <span v-if="row.client_duration">{{ row.client_duration }} hrs</span>
              <span v-else class="text-muted-custom">-</span>
            </template>
            <template v-else>
              <span v-if="row.duration">{{ row.duration }} hrs</span>
              <span v-else class="text-muted-custom">-</span>
            </template>
          </template>

          <template #cell-hours_applied="{ row }">
            <template v-if="isDSPDGroupAuth">
              <span>{{ row.units_applied_to_auth ?? '1' }}</span>
            </template>
            <template v-else>
              <span>{{ row.hours_applied_to_auth || row.billing_hours || '0.00' }} hrs</span>
            </template>
          </template>

          <template #cell-approved_at="{ row }">
            <small>{{ cleanTimeString(row.approved_at) || 'N/A' }}</small>
          </template>

          <template #cell-shift_notes="{ row }">
            <div v-if="row.shift_notes" class="shift-notes-preview">
              <span :title="row.shift_notes">
                {{ truncateText(row.shift_notes, 50) }}
              </span>
            </div>
            <span v-else class="text-muted-custom">No notes</span>
          </template>

          <template #cell-actions="{ row }">
            <button
              v-if="canChangeServiceCode(row)"
              class="btn btn-sm btn-primary"
              @click="openChangeServiceCodeModal(row)"
              title="Change Service Code"
            >
              <i class="fa-solid fa-code"></i>
            </button>
          </template>
        </AIVTable>
      </div>

      <div v-else class="text-center py-5">
        <i class="fa-solid fa-info-circle text-muted-custom" style="font-size: 3rem;"></i>
        <h5 class="mt-3 text-muted-custom">No Approved Shifts</h5>
        <p class="text-muted-custom">This authorization has no approved shifts yet.</p>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button
          v-if="shouldShowMigrateButton"
          type="primary"
          @click="openMigrateFutureAuthModal"
          title="Migrate shifts from future authorizations"
        >
          <i class="fa-solid fa-arrows-turn-to-dots mr-1"></i>
          Migrate Future Auth Shifts
        </el-button>
        <el-button @click="closeModal" :style="shouldShowMigrateButton ? 'margin-left: 10px' : ''">Close</el-button>
      </div>
    </template>

    <ChangeServiceCodeModal
      :visible.sync="showChangeServiceCodeModal"
      :shift="shiftToChangeServiceCode"
      @service-code-changed="handleServiceCodeChanged"
    />

    <AttachFutureAuthModal
      :visible="showAttachFutureAuthModal"
      :client-id="authorizationData?.id_clients"
      :service-code-id="authorizationData?.id_service_codes"
      :authorization-id="authorizationData?.id"
      :payer-type="attachModalPayerType"
      @update:visible="showAttachFutureAuthModal = $event"
      @attached="handleFutureAuthAttached"
    />
  </AIVDialog>
</template>

<script>
import { shiftsService } from "src/serviceHandlers/shiftsService";
import ChangeServiceCodeModal from "@/components/ChangeServiceCodeModal.vue";
import AttachFutureAuthModal from "@/components/AttachFutureAuthModal.vue";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "ApprovedShiftsModal",
  components: {
    ChangeServiceCodeModal,
    AttachFutureAuthModal,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    authorizationData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      shiftsData: null,
      shiftColumns: [
        { key: "date", label: "Date", width: "100px" },
        { key: "time_range", label: "Scheduled Time", width: "140px" },
        { key: "clock_times", label: "Actual Time", width: "180px" },
        { key: "caregivers", label: "Caregivers", width: "150px" },
        { key: "clients", label: "Clients", width: "150px" },
        { key: "service_codes", label: "Services", width: "120px" },
        { key: "duration", label: "Duration", width: "80px" },
        { key: "hours_applied", label: "Applied Hours", width: "100px" },
        { key: "approved_at", label: "Approved", width: "120px" },
        { key: "shift_notes", label: "Notes", width: "200px" },
        { key: "actions", label: "Actions", width: "120px", sortable: false }
      ],
      showChangeServiceCodeModal: false,
      shiftToChangeServiceCode: null,
      showAttachFutureAuthModal: false
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    },
    isDSPDGroupAuth() {
      return (
        this.authorizationData?.units !== null &&
        this.authorizationData?.units !== undefined
      );
    },
    effectiveColumns() {
      return this.shiftColumns.map((col) => {
        if (col.key === "hours_applied" && this.isDSPDGroupAuth) {
          return { ...col, label: "Applied Units" };
        }
        return col;
      });
    },
    userRole() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type || null;
    },
    paginationObject() {
      // Return a proper pagination object for AIVTable
      // Since we load all shifts at once, we don't need actual pagination
      const total = this.shiftsData?.count || 0;
      return {
        total: total,
        totalPages: 1,
        currentPage: 1,
        perPage: total
      };
    },
    shouldShowMigrateButton() {
      // Only show the button if:
      // 1. User is Admin
      // 2. It's a real (non-sentinel) auth — sentinel names start with 'Future'
      // 3. It's a VR auth OR a unit-based DSPD/PP budget
      if (this.userRole !== 'Admin') return false;
      if (!this.authorizationData) return false;

      // Sentinel Future auths (bare 'Future' or 'Future-…') can't be a target
      const authNumber = this.authorizationData.auth_number || '';
      if (authNumber.startsWith('Future')) return false;

      // VR auth: payer_type 'VR' or auth_number starting with 'V'
      const payerType = this.authorizationData.client?.payer_type;
      const isVRAuth = payerType === 'VR' || authNumber.startsWith('V');

      // Unit-based DSPD/PP budget: has a units value (group and 1-on-1 alike)
      return isVRAuth || this.authorizationData.units != null;
    },
    attachModalPayerType() {
      // Unit-based budgets get the units presentation in the attach modal
      return this.authorizationData?.units != null ? 'DSPD' : 'VR';
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.authorizationData?.id) {
        this.fetchApprovedShifts();
      }
    },
    authorizationData(newVal) {
      if (newVal?.id && this.visible) {
        this.fetchApprovedShifts();
      }
    }
  },
  methods: {
    async fetchApprovedShifts() {
      if (!this.authorizationData?.id) {
        this.error = "No authorization selected";
        return;
      }

      this.loading = true;
      this.error = null;
      this.shiftsData = null;

      try {
        const response = await shiftsService.getApprovedShiftsByAuthorization(
          this.authorizationData.id
        );
        this.shiftsData = response;
      } catch (error) {
        this.error = "Failed to load approved shifts: " + error.message;
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    formatTimeRange(startDateTime, endDateTime) {
      if (!startDateTime || !endDateTime) return "N/A";

      const formatTime = (dateTimeStr) => {
        const date = new Date(dateTimeStr);
        return date.toLocaleTimeString("en-US", {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      };

      return `${formatTime(startDateTime)} - ${formatTime(endDateTime)}`;
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    cleanTimeString(timeString) {
      if (!timeString) return "";
      // Remove the " z" suffix if it exists
      return timeString.replace(/ z$/, "");
    },

    formatBreakTime(breakStart, breakEnd) {
      if (!breakStart || !breakEnd) return "N/A";
      const start = this.cleanTimeString(breakStart);
      const end = this.cleanTimeString(breakEnd);
      // Extract just the time portion (without the date)
      const startTime = start.split(' ').slice(1).join(' ');
      const endTime = end.split(' ').slice(1).join(' ');
      return `${startTime} - ${endTime}`;
    },

    closeModal() {
      this.$emit('update:visible', false);
    },

    canChangeServiceCode(shift) {
      // Only Admins can change service codes
      if (this.userRole !== "Admin") return false;

      // Must have service codes and billing hours
      if (!shift.service_codes || shift.service_codes.length === 0) return false;
      if (!shift.billing_hours || parseFloat(shift.billing_hours) <= 0) return false;

      return true;
    },

    openChangeServiceCodeModal(shift) {
      if (!this.canChangeServiceCode(shift)) {
        this.$notifyError("You don't have permission to change the service code for this shift");
        return;
      }

      // Map the shift data for the modal
      const mappedShift = {
        id: shift.id,
        date: shift.date || shift.scheduled_start,
        startTime: shift.scheduled_start,
        endTime: shift.scheduled_end,
        scheduled_start: shift.scheduled_start,
        scheduled_end: shift.scheduled_end,
        billing_hours: shift.billing_hours,
        service_codes: shift.service_codes,
        serviceCodes: shift.service_codes,
        payer: shift.payer,
        shift_type: shift.shift_type,
        clients: shift.clients,
        client: shift.clients && shift.clients.length > 0
          ? shift.clients[0].name
          : "Unassigned",
        caregivers: shift.caregivers,
        caregiver: shift.caregivers && shift.caregivers.length > 0
          ? shift.caregivers[0].name
          : "Unassigned",
      };

      this.shiftToChangeServiceCode = mappedShift;
      this.showChangeServiceCodeModal = true;
    },

    handleServiceCodeChanged() {
      // Reload the shifts to reflect the change
      this.fetchApprovedShifts();
      // Notify parent component to refresh authorization data
      this.$emit('authorization-data-changed');
    },

    openMigrateFutureAuthModal() {
      if (!this.authorizationData?.id_clients || !this.authorizationData?.id) {
        this.$notifyError("Authorization data is incomplete");
        return;
      }
      this.showAttachFutureAuthModal = true;
    },

    handleFutureAuthAttached(result) {
      // Reload the shifts to reflect the migrated hours
      this.fetchApprovedShifts();
      // Notify parent component to refresh authorization data
      this.$emit('authorization-data-changed');
    }
  }
};
</script>

<style scoped>
.modal-content-wrapper {
  max-height: calc(85vh - 120px);
  overflow-y: auto;
  overflow-x: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.shift-notes-preview {
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Summary Info Styling */
.summary-info {
  background-color: rgba(37, 55, 65, 0.7);
  color: #e0e0e0;
  border: 1px solid rgba(66, 98, 116, 0.5);
}


/* Custom text-muted for dark theme */
.text-muted-custom {
  color: #a0a0a0 !important;
}

:deep(.el-dialog__body) {
  padding: 15px 20px;
}

.mr-1 {
  margin-right: 0.25rem;
}
</style>